import "./pages.css";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import config from "../Config";
import StorageIcon from "@mui/icons-material/Storage";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DnsIcon from "@mui/icons-material/Dns";
import LanguageIcon from "@mui/icons-material/Language";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BuildIcon from "@mui/icons-material/Build";
import LockIcon from "@mui/icons-material/Lock";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ApiIcon from "@mui/icons-material/Api";
import RouterIcon from "@mui/icons-material/Router";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import OfflineBoltIcon from "@mui/icons-material/OfflineBolt";
import DomainVerificationIcon from "@mui/icons-material/DomainVerification";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

const FEATURES = [
  {
    Icon: StorageIcon,
    tab: "Run",
    title: "Isolated Cluster",
    link: "/documentation#cluster-overview",
    color: "#6c5ce7",
    points: [
      "Spin up a private Kubernetes cluster in minutes, on your own nodes",
      "Your own isolated VPC network — not namespace partitioning",
      "A dedicated control API for every cluster you create",
      "Scale node pools without writing a line of Kubernetes",
    ],
    visual: {
      title: "cluster · dedicated",
      cols: ["Node", "Status"],
      rows: [
        { k: "ip-10-0-1-42", v: "Ready", tone: "green" },
        { k: "ip-10-0-2-17", v: "Ready", tone: "green" },
        { k: "ip-10-0-3-08", v: "Provisioning", tone: "amber" },
        { k: "dedicated-api", v: "Healthy", tone: "green" },
      ],
    },
  },
  {
    Icon: RocketLaunchIcon,
    tab: "Jobs",
    title: "Dedicated API",
    link: "/documentation#api",
    color: "#00b894",
    points: [
      "Submit any Docker container through a single API call",
      "Parallelise batch workloads, ETL pipelines, and data processing at any scale",
      "Billed per second — you never pay for idle capacity",
      "Reach for Premium GPU nodes on the jobs that need them",
    ],
    visual: {
      title: "POST /v1/mcr/create-job",
      cols: ["Job", "State"],
      rows: [
        { k: "job-7f3a9c2d", v: "Running", tone: "blue" },
        { k: "job-2b81ee40", v: "Succeeded", tone: "green" },
        { k: "job-9c04a1f7", v: "Succeeded", tone: "green" },
        { k: "job-55de0b13", v: "Queued", tone: "dim" },
      ],
    },
  },
  {
    Icon: DnsIcon,
    tab: "Deploy",
    title: "Long-Running Deployments",
    link: "/documentation#deploy-overview",
    color: "#0984e3",
    points: [
      "Always-on containerised services running alongside your jobs",
      "Auto-scaling and health checks handled for you",
      "Live traffic, logs, and metrics in one place",
      "No Kubernetes manifests to write or maintain",
    ],
    visual: {
      title: "deployments",
      cols: ["Service", "Replicas"],
      rows: [
        { k: "api-service", v: "3 / 3", tone: "green" },
        { k: "worker-pool", v: "2 / 2", tone: "green" },
        { k: "web-frontend", v: "1 / 1", tone: "green" },
        { k: "batch-runner", v: "0 / 1", tone: "dim" },
      ],
    },
  },
  {
    Icon: CloudUploadIcon,
    tab: "Registry",
    title: "Container Registry",
    link: "/documentation#mcr",
    color: "#e17055",
    points: [
      "Push private images to the Mermadic Container Registry",
      "Pull them straight into jobs and deployments — no external registry required",
      "Images stay inside the platform, so there is no cross-cloud egress to pay for",
    ],
    visual: {
      title: "mcr · my-team",
      cols: ["Image", "Tag"],
      rows: [
        { k: "my-app", v: "v1.2.0", tone: "blue" },
        { k: "etl-worker", v: "v0.9.4", tone: "blue" },
        { k: "nginx-proxy", v: "latest", tone: "blue" },
        { k: "model-server", v: "gpu-cu12", tone: "blue" },
      ],
    },
  },
  {
    Icon: AutorenewIcon,
    tab: "CDCI",
    title: "CDCI Automation",
    link: "/documentation#cdci-overview",
    color: "#fdcb6e",
    points: [
      "Connect a GitHub repository in a couple of clicks",
      "Build, push, and deploy automatically on every commit or tag",
      "Ship from your normal git workflow with no manual steps in between",
    ],
    visual: {
      title: "pipeline · main",
      cols: ["Stage", "Result"],
      rows: [
        { k: "checkout", v: "Passed", tone: "green" },
        { k: "build image", v: "Passed", tone: "green" },
        { k: "push to mcr", v: "Passed", tone: "green" },
        { k: "deploy", v: "Running", tone: "blue" },
      ],
    },
  },
  {
    Icon: LanguageIcon,
    tab: "Route",
    title: "Custom Domains",
    link: "/documentation#deploy-domains",
    color: "#a29bfe",
    points: [
      "Map your own domain to any deployment",
      "Automatic TLS termination, issued and renewed for you",
      "Fully managed routing — your services, your branding",
    ],
    visual: {
      title: "domains",
      cols: ["Domain", "TLS"],
      rows: [
        { k: "api.acme.com", v: "Active", tone: "green" },
        { k: "app.acme.com", v: "Active", tone: "green" },
        { k: "staging.acme.com", v: "Issuing", tone: "amber" },
      ],
    },
  },
  {
    Icon: VpnKeyIcon,
    tab: "Secure",
    title: "Encrypted Secrets",
    link: "/documentation#deploy-secrets",
    color: "#00ff7f",
    points: [
      "Store API keys, passwords, and tokens encrypted at rest",
      "Inject them into jobs, cron schedules, and deployments as environment variables",
      "Write-only by design — not even we can read a value back",
    ],
    visual: {
      title: "secrets · production",
      cols: ["Key", "Value"],
      rows: [
        { k: "API_KEY", masked: true },
        { k: "MF_MONGO_HOST", masked: true },
        { k: "MF_MONGO_USERNAME", masked: true },
        { k: "MF_MONGO_PASSWORD", masked: true },
      ],
    },
  },
];

const BYOC_ADVANTAGES = [
  {
    Icon: RouterIcon,
    tab: "Isolated VPC",
    color: "#0984e3",
    title: "Dedicated Isolated VPC Per Customer",
    points: [
      "Every dedicated cluster runs inside its own private VPC",
      "Workloads are separated at the network layer, not by namespace or label",
      "No other tenant's traffic ever reaches your subnets",
      "The VPC is torn down with your last cluster",
    ],
    visual: {
      title: "vpc-0a1b2c · dedicated",
      cols: ["Resource", "State"],
      rows: [
        { k: "vpc-0a1b2c", v: "Active", tone: "green" },
        { k: "subnet-private-a", v: "Attached", tone: "blue" },
        { k: "subnet-private-b", v: "Attached", tone: "blue" },
        { k: "nat-gateway", v: "Healthy", tone: "green" },
      ],
    },
  },
  {
    Icon: CloudOffIcon,
    tab: "No cloud account",
    color: "#6c5ce7",
    title: "No Cloud Account Required",
    points: [
      "No AWS account to create, fund, or manage",
      "No IAM roles, policies, or trust relationships to configure",
      "No VPC networking knowledge needed to get started",
    ],
    visual: {
      title: "what you have to set up",
      cols: ["Requirement", "You"],
      rows: [
        { k: "AWS account", v: "Not needed", tone: "dim" },
        { k: "IAM roles", v: "Not needed", tone: "dim" },
        { k: "VPC design", v: "Not needed", tone: "dim" },
        { k: "Mermadic login", v: "That's it", tone: "green" },
      ],
    },
  },
  {
    Icon: RocketLaunchIcon,
    tab: "Fast onboarding",
    color: "#00b894",
    title: "Faster Enterprise Onboarding",
    points: [
      "Provisioned from a known-good infrastructure pattern every time",
      "Consistent and secure by default across every environment",
      "Ready in minutes, without a custom cloud setup project",
    ],
    visual: {
      title: "provisioning · elapsed",
      cols: ["Step", "Time"],
      rows: [
        { k: "vpc + subnets", v: "0:42", tone: "green" },
        { k: "control plane", v: "3:15", tone: "green" },
        { k: "node pool", v: "5:08", tone: "green" },
        { k: "dedicated api", v: "6:31", tone: "green" },
      ],
    },
  },
  {
    Icon: SupportAgentIcon,
    tab: "Managed ops",
    color: "#e17055",
    title: "Fully Managed Operations",
    points: [
      "Provisioning, upgrades, scaling, monitoring, and incident response",
      "Mermadic owns the full infrastructure stack end to end",
      "One vendor and clear accountability when something goes wrong",
    ],
    visual: {
      title: "who owns what",
      cols: ["Layer", "Owner"],
      rows: [
        { k: "Infrastructure", v: "Mermadic", tone: "green" },
        { k: "Kubernetes", v: "Mermadic", tone: "green" },
        { k: "Platform + support", v: "Mermadic", tone: "green" },
        { k: "Your application", v: "You", tone: "blue" },
      ],
    },
  },
  {
    Icon: HealthAndSafetyIcon,
    tab: "Recovery",
    color: "#a29bfe",
    title: "Self-Managed Disaster Recovery",
    points: [
      "Trigger recovery workflows yourself without waiting on support",
      "Mermadic maintains the underlying recovery architecture",
      "Data integrity is preserved across every recovery path",
    ],
    visual: {
      title: "cluster recovery",
      cols: ["Action", "Available to"],
      rows: [
        { k: "Restart dedicated API", v: "You", tone: "green" },
        { k: "Rebuild node pool", v: "You", tone: "green" },
        { k: "Restore cluster state", v: "You", tone: "green" },
        { k: "Recovery architecture", v: "Mermadic", tone: "blue" },
      ],
    },
  },
  {
    Icon: AttachMoneyIcon,
    tab: "One bill",
    color: "#fdcb6e",
    title: "One Platform, One Bill",
    points: [
      "Infrastructure, platform, and support in a single commercial relationship",
      "No separate cloud bills to reconcile at the end of the month",
      "No variable egress surprises",
    ],
    visual: {
      title: "monthly invoice",
      cols: ["Line item", "Billed by"],
      rows: [
        { k: "Cluster tier", v: "Mermadic", tone: "green" },
        { k: "Compute usage", v: "Mermadic", tone: "green" },
        { k: "Network egress", v: "Included", tone: "green" },
        { k: "Cloud provider", v: "No bill", tone: "dim" },
      ],
    },
  },
];

/** Fake console surface shown beside the active tab — decorative only. */
function ShowcaseVisual({ visual }) {
  return (
    <div className="hv2-nf-mock">
      <div className="hv2-nf-mock-bar">
        <span className="hv2-nf-mock-dot" />
        <span className="hv2-nf-mock-caption">{visual.title}</span>
      </div>
      <div className="hv2-nf-mock-head">
        <span>{visual.cols[0]}</span>
        <span>{visual.cols[1]}</span>
      </div>
      {visual.rows.map((row) => (
        <div className="hv2-nf-mock-row" key={row.k}>
          <span className="hv2-nf-mock-k">{row.k}</span>
          {row.masked ? (
            <span className="hv2-nf-mock-mask" />
          ) : (
            <span className={`hv2-nf-mock-v hv2-nf-mock-v--${row.tone}`}>{row.v}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Vertical tab rail driving a single detail panel, in the style of the hero
    feature walkthroughs on modern platform sites. */
function Showcase({ eyebrow, title, sub, items, onNavigate }) {
  const [active, setActive] = useState(0);
  const item = items[active];
  const { Icon } = item;
  const slug = eyebrow.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  return (
    <div className="hv2-nf">
      <div className="hv2-nf-head">
        <div className="hv2-nf-eyebrow">{eyebrow}</div>
        <h2 className="hv2-nf-title">{title}</h2>
        {sub && <p className="hv2-nf-sub">{sub}</p>}
      </div>

      <div className="hv2-nf-body">
        <div className="hv2-nf-rail" role="tablist" aria-label={eyebrow}>
          {items.map((it, i) => (
            <button
              key={it.title}
              type="button"
              role="tab"
              id={`${slug}-tab-${i}`}
              aria-selected={i === active}
              aria-controls={`${slug}-panel`}
              className={`hv2-nf-tab${i === active ? " hv2-nf-tab--active" : ""}`}
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
            >
              <span className="hv2-nf-diamond">
                <it.Icon fontSize="inherit" />
              </span>
              <span className="hv2-nf-tab-label">{it.tab}</span>
            </button>
          ))}
        </div>

        <div
          className="hv2-nf-panel"
          role="tabpanel"
          id={`${slug}-panel`}
          aria-labelledby={`${slug}-tab-${active}`}
        >
          <div className="hv2-nf-panel-main">
            <h3 className="hv2-nf-panel-title">
              <Icon fontSize="inherit" style={{ color: item.color }} />
              {item.title}
            </h3>
            <ul className="hv2-nf-points">
              {item.points.map((p) => (
                <li key={p}>
                  <span className="hv2-nf-chev" aria-hidden="true">»</span>
                  {p}
                </li>
              ))}
            </ul>
            {item.link && (
              <button
                type="button"
                className="hv2-nf-panel-link"
                onClick={() => onNavigate(item.link)}
              >
                Read the docs
                <ArrowForwardIcon fontSize="inherit" />
              </button>
            )}
          </div>
          <div className="hv2-nf-panel-visual">
            <ShowcaseVisual visual={item.visual} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    fetch(`${config.MASTER_API}/v1/billing/pricing`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setPricing(data); })
      .catch(() => {});
  }, []);

  /** Tier fees are owned by the billing API — never hardcode them here. */
  const monthlyPrice = (tierId) => {
    const val = pricing?.pricing?.[tierId];
    return val != null ? `$${val} / month` : "…";
  };

  return (
    <div className="home-v2">

      {/* ── Announcement ── */}
      <section className="hv2-announce">
        <div className="hv2-announce-inner">
          <span className="hv2-announce-tag">New</span>
          <span className="hv2-announce-text">
            Sign up and get <strong>$5 in free credit</strong> to start running jobs.
          </span>
          <button
            type="button"
            className="hv2-announce-cta"
            onClick={() => navigate("/signup")}
          >
            Claim it
            <ArrowForwardIcon fontSize="inherit" />
          </button>
        </div>
      </section>

      {/* ── Hero ── */}
      <section className="hv2-hero">
        <div className="hv2-hero-content">
          <div className="hv2-badge">Mermadic: Managed cloud infrastructure</div>
          <h1 className="hv2-headline">
            Run jobs. Deploy services.<br />
            Ship faster — fully managed, fully isolated.
          </h1>
          <p className="hv2-subtext">
            One platform for batch jobs, long-running APIs, CI/CD automation, and private container
            images. Every cluster is a <strong>truly isolated environment</strong> — your own private VPC network,
            isolated compute, and a dedicated API per cluster. Not just isolated pods — isolated infrastructure.
          </p>
          <div className="hv2-hero-actions">
            <button className="hv2-btn-primary" onClick={() => navigate("/signup")}>
              Get started free
            </button>
            <button className="hv2-btn-ghost" onClick={() => navigate("/pricing")}>
              See pricing
            </button>
          </div>
        </div>
        <div className="hv2-hero-img">
          <div className="hv2-terminal">
            <div className="hv2-terminal-bar">
              <span className="hv2-terminal-dot hv2-terminal-dot--red" />
              <span className="hv2-terminal-dot hv2-terminal-dot--yellow" />
              <span className="hv2-terminal-dot hv2-terminal-dot--green" />
              <span className="hv2-terminal-title">mermadic · dedicated API</span>
            </div>
            <div className="hv2-terminal-body">
              <div className="hv2-t-lines">
                <div className="hv2-tl"><span className="hv2-t-dim">$</span> <span className="hv2-t-cmd">curl</span> <span className="hv2-t-dim">-X POST \</span></div>
                <div className="hv2-tl hv2-tl--i2"><span className="hv2-t-url">https://job.mermadicrunner.com/job-runner/v1/mcr/create-job</span> <span className="hv2-t-dim">\</span></div>
                <div className="hv2-tl hv2-tl--i2"><span className="hv2-t-key">-H</span> <span className="hv2-t-str">"Authorization: Bearer $TOKEN"</span> <span className="hv2-t-dim">\</span></div>
                <div className="hv2-tl hv2-tl--i2"><span className="hv2-t-key">-d</span> <span className="hv2-t-dim">{'\'{'}</span></div>
                <div className="hv2-tl hv2-tl--i4"><span className="hv2-t-str">"image"</span><span className="hv2-t-dim">:</span> <span className="hv2-t-str">"my-app:v1.2.0"</span><span className="hv2-t-dim">,</span></div>
                <div className="hv2-tl hv2-tl--i4"><span className="hv2-t-str">"commandArgs"</span><span className="hv2-t-dim">:</span> [<span className="hv2-t-str">"--workers"</span><span className="hv2-t-dim">,</span> <span className="hv2-t-str">"4"</span>]<span className="hv2-t-dim">,</span></div>
                <div className="hv2-tl hv2-tl--i4"><span className="hv2-t-str">"resourceRequests"</span><span className="hv2-t-dim">:</span> <span className="hv2-t-dim">{"{"}</span><span className="hv2-t-str">"cpu"</span><span className="hv2-t-dim">:</span> <span className="hv2-t-num">2</span><span className="hv2-t-dim">,</span> <span className="hv2-t-str">"memory"</span><span className="hv2-t-dim">:</span> <span className="hv2-t-num">4</span><span className="hv2-t-dim">{"}"}</span></div>
                <div className="hv2-tl hv2-tl--i2"><span className="hv2-t-dim">{"}'}"}</span></div>
              </div>
              <div className="hv2-t-spacer" />
              <div className="hv2-t-lines">
                <div className="hv2-tl"><span className="hv2-t-dim">&gt;</span> <span className="hv2-t-str">"job-7f3a9c2d"</span></div>
              </div>
              <div className="hv2-t-spacer" />
              <div className="hv2-terminal-status">
                <div className="hv2-ts-row">
                  <span className="hv2-ts-dot hv2-ts-dot--green" />
                  <span className="hv2-ts-label">cluster</span>
                  <span className="hv2-ts-val hv2-ts-val--green">active</span>
                </div>
                <div className="hv2-ts-row">
                  <span className="hv2-ts-dot hv2-ts-dot--green" />
                  <span className="hv2-ts-label">job-7f3a9c2d</span>
                  <span className="hv2-ts-val hv2-ts-val--green">running</span>
                </div>
                <div className="hv2-ts-row">
                  <span className="hv2-ts-dot hv2-ts-dot--blue" />
                  <span className="hv2-ts-label">api-service</span>
                  <span className="hv2-ts-val hv2-ts-val--blue">deployed</span>
                </div>
                <div className="hv2-ts-row">
                  <span className="hv2-ts-dot hv2-ts-dot--purple" />
                  <span className="hv2-ts-label">domain</span>
                  <span className="hv2-ts-val hv2-ts-val--purple">propagated</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── All Features ── */}
      <section className="hv2-section hv2-nf-section">
        <Showcase
          eyebrow="With you at every step"
          title={<>One platform for<br />everything you ship</>}
          sub="One account covering your entire container lifecycle."
          items={FEATURES}
          onNavigate={navigate}
        />
      </section>

      {/* ── Single-Tenant Isolation ── */}
      <section className="hv2-section hv2-isolation-section">
        <h2 className="hv2-section-title">Infrastructure isolation</h2>
        <p className="hv2-section-sub">
          Every account gets its own private VPC, its own cluster, and its own nodes.
          Isolating at the infrastructure layer delivers stronger security and more consistent
          performance than namespace-level isolation within a shared cluster.
        </p>
        <div className="hv2-cards hv2-cards-4">
          <div className="hv2-card hv2-card-isolation hv2-card-isolation--highlight">
            <div className="hv2-card-icon" style={{ background: "#0984e31a", color: "#0984e3" }}>
              <RouterIcon fontSize="large" />
            </div>
            <h3>Isolated Private VPC</h3>
            <p>
              Your clusters run inside a private AWS VPC provisioned exclusively for your account.
              No other customer's traffic ever touches your subnets — not even on the same node.
              When your last cluster is deleted, the VPC is torn down with it.
            </p>
            <span className="hv2-isolation-badge">Network-level isolation</span>
          </div>
          <div className="hv2-card hv2-card-isolation">
            <div className="hv2-card-icon" style={{ background: "#6c5ce71a", color: "#6c5ce7" }}>
              <AccountTreeIcon fontSize="large" />
            </div>
            <h3>Isolated Cluster</h3>
            <p>
              Your workloads run on your own nodes in a Kubernetes cluster provisioned only for
              you — compute completely separate from every other tenant.
            </p>
          </div>
          <div className="hv2-card hv2-card-isolation">
            <div className="hv2-card-icon" style={{ background: "#00b8941a", color: "#00b894" }}>
              <ApiIcon fontSize="large" />
            </div>
            <h3>Dedicated API Per Cluster</h3>
            <p>
              Each cluster gets its own dedicated control API endpoint running on its own nodes. No shared
              request queues, no rate limits imposed by other tenants' traffic.
            </p>
          </div>
          <div className="hv2-card hv2-card-isolation">
            <div className="hv2-card-icon" style={{ background: "#e170551a", color: "#e17055" }}>
              <LockIcon fontSize="large" />
            </div>
            <h3>Zero Shared Compute</h3>
            <p>
              Your nodes run only your workloads. CPU and memory are never
              shared — consistent performance, complete data isolation.
            </p>
          </div>
        </div>
      </section>

      {/* ── BYOC Comparison ── */}
      <section className="hv2-section hv2-nf-section hv2-byoc-section">
        <Showcase
          eyebrow="Dedicated tier · vs BYOC"
          title={<>The benefits of BYOC,<br />without managing the cloud</>}
          sub="A private, isolated VPC architecture without bringing, configuring, or operating your own cloud account — while Mermadic handles the infrastructure, networking, scaling, and reliability behind the scenes."
          items={BYOC_ADVANTAGES}
          onNavigate={navigate}
        />

        <div className="hv2-byoc-callout">
          <div className="hv2-byoc-callout-icon">
            <OfflineBoltIcon fontSize="large" />
          </div>
          <div className="hv2-byoc-callout-body">
            <h3>Your cluster runs independently — even when we don't</h3>
            <p>
              Your dedicated API is deployed directly on your own cluster nodes — not on Mermadic's
              shared infrastructure. All running deployments and workloads operate from within your
              isolated environment with no dependency on Mermadic's central services. Even during
              a Mermadic service interruption, your cluster stays live and your applications keep running.
            </p>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="hv2-section hv2-steps-section">
        <h2 className="hv2-section-title">Up and running in five steps</h2>
        <div className="hv2-steps">
          <div className="hv2-step">
            <div className="hv2-step-num">1</div>
            <StorageIcon className="hv2-step-icon" fontSize="large" />
            <h4>Create a cluster</h4>
            <p>Provision your own isolated Kubernetes cluster in its own private VPC, with a dedicated API per cluster — ready in minutes.</p>
          </div>
          <div className="hv2-step-arrow">→</div>
          <div className="hv2-step">
            <div className="hv2-step-num">2</div>
            <CloudUploadIcon className="hv2-step-icon" fontSize="large" />
            <h4>Push your image</h4>
            <p>Upload to the Mermadic Container Registry or point to any public image — it's ready to run immediately.</p>
          </div>
          <div className="hv2-step-arrow">→</div>
          <div className="hv2-step">
            <div className="hv2-step-num">3</div>
            <DomainVerificationIcon className="hv2-step-icon" fontSize="large" />
            <h4>Point your domain <span className="hv2-step-optional">optional</span></h4>
            <p>Configure a deployment with your own domain and add the provided CNAME to your DNS provider. Automatic TLS included.</p>
          </div>
          <div className="hv2-step-arrow">→</div>
          <div className="hv2-step">
            <div className="hv2-step-num">4</div>
            <BuildIcon className="hv2-step-icon" fontSize="large" />
            <h4>Run a job or deploy a service</h4>
            <p>Submit a one-off job via API, or launch an always-on deployment — your call.</p>
          </div>
          <div className="hv2-step-arrow">→</div>
          <div className="hv2-step">
            <div className="hv2-step-num">5</div>
            <PlayCircleOutlineIcon className="hv2-step-icon" fontSize="large" />
            <h4>Automate with CDCI</h4>
            <p>Connect GitHub to trigger builds and deployments on every push. Ship faster with zero manual steps.</p>
          </div>
        </div>
      </section>

      {/* ── Pricing callout ── */}
      <section className="hv2-section hv2-pricing-callout">
        <div className="hv2-pricing-grid">
          <div className="hv2-pricing-item">
            <AttachMoneyIcon fontSize="large" />
            <h4>Shared — Free to start</h4>
            <p>Run jobs and push images with no monthly commitment. Pay only for what you run — and every new account starts with <strong>$5 in free credit</strong>.</p>
          </div>
          <div className="hv2-pricing-item">
            <AttachMoneyIcon fontSize="large" />
            <h4>Gold — {monthlyPrice("dev")}</h4>
            <p>Isolated cluster, <strong>isolated private VPC</strong>, and a dedicated API per cluster. Auto-scaling medium nodes with full CDCI and deployment support.</p>
          </div>
          <div className="hv2-pricing-item">
            <AttachMoneyIcon fontSize="large" />
            <h4>Premium — {monthlyPrice("prod")}</h4>
            <p>Isolated cluster, <strong>isolated private VPC</strong>, and a dedicated API per cluster. Always-on xlarge nodes with burst capacity for production workloads.</p>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <button className="hv2-btn-outline-dark" onClick={() => navigate("/pricing")}>
            View full pricing breakdown
          </button>
        </div>
      </section>

      {/* ── Learn more (internal links for crawlers and visitors) ── */}
      <section className="hv2-section hv2-explore">
        <h2 className="hv2-section-title">Learn more</h2>
        <div className="hv2-explore-links">
          <Link to="/kubernetes-without-aws" className="hv2-explore-link">
            Run Kubernetes without an AWS account
          </Link>
          <Link to="/batch-jobs" className="hv2-explore-link">
            Run Docker batch jobs through one API call
          </Link>
          <Link to="/gpu-jobs" className="hv2-explore-link">
            GPU batch jobs, billed by the second
          </Link>
          <Link to="/pricing" className="hv2-explore-link">
            See dedicated cluster pricing
          </Link>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hv2-cta-section">
        <h2>Start building on Mermadic Solutions today</h2>
        <p>Create a free account, spin up your cluster, and have your first container running in minutes.</p>
        <div className="hv2-hero-actions">
          <button className="hv2-btn-primary" onClick={() => navigate("/signup")}>
            Create free account
          </button>
          <button className="hv2-btn-ghost hv2-btn-ghost-light" onClick={() => navigate("/documentation")}>
            Read the docs
          </button>
        </div>
      </section>

    </div>
  );
}
